import { Injectable, inject } from '@angular/core'
import { TranslocoService } from '@jsverse/transloco'
import { getAuth } from 'firebase/auth'
import { getFirestore, doc, setDoc, getDoc, onSnapshot, Unsubscribe } from 'firebase/firestore'
import { getFirebaseApp } from './firebase'

export function normalizeLanguage(value: string | null | undefined): 'en' | 'fr' | null {
  if (!value) return null
  const base = value.trim().toLowerCase().split(/[-_]/)[0]
  return base === 'en' || base === 'fr' ? base : null
}

/** Migrate the old `locale` preference without overriding the newer key. */
export function getStoredLanguage(): 'en' | 'fr' | null {
  const current = normalizeLanguage(localStorage.getItem('lang'))
  const legacy = normalizeLanguage(localStorage.getItem('locale'))
  const language = current ?? legacy
  if (language && localStorage.getItem('lang') !== language) {
    localStorage.setItem('lang', language)
  }
  return language
}

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date()
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000)
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const trimmed = cookie.trim()
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length))
      }
    }
  } catch {}
  return null
}

/**
 * Clone of admin-console's `LanguageService` (lives in `translation.service.ts`
 * there too). Persists the active language to localStorage, a `.crewdle.ai`
 * `crewdle_lang` cookie, and the user's Firestore doc
 * (`users/{uid}.language`, with a live `onSnapshot` for cross-tab/device sync).
 */
@Injectable({ providedIn: 'root' })
export class TranslationService {
  private userDocUnsub: Unsubscribe | null = null

  constructor(private translate: TranslocoService) {}

  getLanguage(): string {
    return getStoredLanguage()
      ?? normalizeLanguage(getCrewdleCookie('lang'))
      ?? normalizeLanguage(this.translate.getActiveLang())
      ?? 'fr'
  }

  setLanguage(language: string): void {
    language = normalizeLanguage(language) ?? language
    localStorage.setItem('lang', language)
    setCrewdleCookie('lang', language)
    this.translate.setActiveLang(language)
    this.syncToFirestore(language)
  }

  async loadUserLanguage(): Promise<void> {
    try {
      const app = getFirebaseApp()
      if (!app) return
      const auth = getAuth(app)
      const uid = auth.currentUser?.uid
      if (!uid) return

      const localLang = getStoredLanguage()
      const db = getFirestore(app)
      const snapshot = await getDoc(doc(db, 'users', uid))
      const firestoreLang = snapshot.exists() ? snapshot.data()['language'] : null

      if (localLang && ['en', 'fr'].includes(localLang)) {
        // Local preference exists — push it to Firestore if different
        if (firestoreLang !== localLang) {
          await setDoc(doc(db, 'users', uid), { language: localLang }, { merge: true })
        }
        setCrewdleCookie('lang', localLang)
        this.translate.setActiveLang(localLang)
      } else if (firestoreLang && ['en', 'fr'].includes(firestoreLang)) {
        // No local preference — use Firestore value
        localStorage.setItem('lang', firestoreLang)
        setCrewdleCookie('lang', firestoreLang)
        this.translate.setActiveLang(firestoreLang)
      }
    } catch {}
  }

  /**
   * Apply a language received from Firestore (e.g. agent-driven change).
   * Updates local caches but does not re-sync to Firestore.
   */
  private applyRemoteLanguage(lang: string): void {
    if (lang === this.translate.getActiveLang()) return
    localStorage.setItem('lang', lang)
    setCrewdleCookie('lang', lang)
    this.translate.setActiveLang(lang)
  }

  startUserSync(uid: string): void {
    const app = getFirebaseApp()
    if (!app) return
    this.stopUserSync()
    const db = getFirestore(app)
    this.userDocUnsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (!snap.exists()) return
      const remote = snap.data()['language']
      if (remote && ['en', 'fr'].includes(remote)) {
        this.applyRemoteLanguage(remote)
      }
    })
  }

  stopUserSync(): void {
    if (this.userDocUnsub) {
      this.userDocUnsub()
      this.userDocUnsub = null
    }
  }

  private async syncToFirestore(lang: string): Promise<void> {
    try {
      const app = getFirebaseApp()
      if (!app) return
      const auth = getAuth(app)
      const uid = auth.currentUser?.uid
      if (!uid) return

      const db = getFirestore(app)
      await setDoc(doc(db, 'users', uid), { language: lang }, { merge: true })
    } catch {}
  }
}
